"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faHeart,
  faBookmark,
  faSpinner,
  faArrowLeft,
  faArrowRight,
  faExternalLinkAlt,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/utils/supabase/client";

export default function DiscoveryMode({ isOpen, onClose, session }) {
  const { toast } = useToast();
  const [spaces, setSpaces] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [seenSpaces, setSeenSpaces] = useState(new Set());
  const [endReached, setEndReached] = useState(false);

  // Touch handling refs
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const isDragging = useRef(false);
  const cardRef = useRef(null);
  const isSwipeInProgress = useRef(false);

  // Animation state
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragRotation, setDragRotation] = useState(0);

  const currentSpace = spaces[currentIndex] || null;

  // Fetch discovery spaces from edge function
  const fetchDiscoverySpaces = useCallback(
    async (excludeIds = []) => {
      try {
        setLoading(true);

        const response = await fetch(
          "/api/supabase/functions/discovery-spaces",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              user_id: session?.user?.id,
              limit: 20,
              exclude_ids: excludeIds,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch discovery spaces");
        }

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        return data.spaces || [];
      } catch (error) {
        console.error("Error fetching discovery spaces:", error);
        toast({
          title: "Error",
          description: "Failed to load spaces for discovery. Please try again.",
          variant: "destructive",
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [session?.user?.id, toast]
  );

  // Initialize spaces on mount
  useEffect(() => {
    if (isOpen) {
      const initializeSpaces = async () => {
        const newSpaces = await fetchDiscoverySpaces();
        setSpaces(newSpaces);
        setCurrentIndex(0);
        setSeenSpaces(new Set());
        setEndReached(false);
      };

      initializeSpaces();
    }
  }, [isOpen, fetchDiscoverySpaces]);

  // Load more spaces when running low
  useEffect(() => {
    const loadMoreSpaces = async () => {
      if (spaces.length - currentIndex <= 3 && !loading && !endReached) {
        const excludeIds = [...seenSpaces, ...spaces.map((s) => s.id)];
        const newSpaces = await fetchDiscoverySpaces(excludeIds);

        if (newSpaces.length === 0) {
          setEndReached(true);
        } else {
          setSpaces((prev) => [...prev, ...newSpaces]);
        }
      }
    };

    loadMoreSpaces();
  }, [
    currentIndex,
    spaces.length,
    seenSpaces,
    loading,
    endReached,
    fetchDiscoverySpaces,
  ]);

  // Handle favorite/bookmark action
  const handleFavoriteSpace = useCallback(
    async (spaceId) => {
      if (!session?.user?.id) {
        toast({
          title: "Sign in required",
          description: "Please sign in to favorite spaces",
          variant: "destructive",
        });
        return;
      }

      try {
        setActionLoading(true);

        const { data, error } = await supabase.rpc("toggle_space_favorite", {
          p_user_id: session.user.id,
          p_space_id: spaceId,
        });

        if (error) throw error;

        toast({
          title: "Added to favorites!",
          description: "This space has been added to your favorites",
          variant: "success",
        });
      } catch (error) {
        console.error("Error favoriting space:", error);
        toast({
          title: "Error",
          description: "Failed to add to favorites. Please try again.",
          variant: "destructive",
        });
      } finally {
        setActionLoading(false);
      }
    },
    [session?.user?.id, toast]
  );

  // Handle next space
  const handleNext = useCallback(
    (action = "skip") => {
      if (!currentSpace || isSwipeInProgress.current) return;

      isSwipeInProgress.current = true;

      // Add current space to seen list
      setSeenSpaces((prev) => new Set([...prev, currentSpace.id]));

      // If it's a like action, favorite the space
      if (action === "like") {
        handleFavoriteSpace(currentSpace.id);
      }

      // Move to next space
      if (currentIndex < spaces.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else if (endReached) {
        // No more spaces
        toast({
          title: "No more spaces",
          description:
            "You've seen all available spaces! Check back later for new discoveries.",
          variant: "default",
        });
      }

      // Reset drag state
      setDragOffset({ x: 0, y: 0 });
      setDragRotation(0);

      // Reset swipe flag after animation
      setTimeout(() => {
        isSwipeInProgress.current = false;
      }, 300);
    },
    [
      currentSpace,
      currentIndex,
      spaces.length,
      endReached,
      handleFavoriteSpace,
      toast,
    ]
  );

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "x" || e.key === "X") {
        e.preventDefault();
        handleNext("skip");
      } else if (e.key === "ArrowRight" || e.key === "h" || e.key === "H") {
        e.preventDefault();
        handleNext("like");
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleNext, onClose]);

  // Enhanced touch handlers for mobile swipe with better isolation
  const handleTouchStart = useCallback(
    (e) => {
      if (actionLoading || isSwipeInProgress.current) return;

      // Complete event isolation to prevent conflicts with global handlers
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const touch = e.touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      touchStartTime.current = Date.now();
      isDragging.current = false;

      console.log("Discovery Mode: Touch start", {
        x: touch.clientX,
        y: touch.clientY,
      });
    },
    [actionLoading]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (actionLoading || isSwipeInProgress.current) return;

      // Complete event isolation
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Only start dragging if horizontal movement is more significant than vertical
      // and we've moved a minimum distance
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 15) {
        isDragging.current = true;

        // Calculate drag offset and rotation
        const maxDrag = 300;
        const clampedDeltaX = Math.max(-maxDrag, Math.min(maxDrag, deltaX));
        const rotation = (clampedDeltaX / maxDrag) * 30; // Max 30 degrees rotation

        setDragOffset({ x: clampedDeltaX, y: deltaY * 0.3 });
        setDragRotation(rotation);

        console.log("Discovery Mode: Dragging", {
          deltaX,
          deltaY,
          clampedDeltaX,
          rotation,
        });
      }
    },
    [actionLoading]
  );

  const handleTouchEnd = useCallback(
    (e) => {
      if (actionLoading || isSwipeInProgress.current) return;

      // Complete event isolation
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;
      const timeDelta = Date.now() - touchStartTime.current;

      console.log("Discovery Mode: Touch end", {
        deltaX,
        deltaY,
        timeDelta,
        isDragging: isDragging.current,
        swipeInProgress: isSwipeInProgress.current,
      });

      if (isDragging.current) {
        // Determine if swipe was significant enough
        const threshold = 80;
        const minTime = 100; // Minimum time to prevent accidental swipes
        const maxTime = 1000; // Maximum time for a valid swipe

        if (
          Math.abs(deltaX) > threshold &&
          timeDelta > minTime &&
          timeDelta < maxTime
        ) {
          if (deltaX > 0) {
            // Swipe right - like
            console.log("Discovery Mode: Swipe right (like)");
            handleNext("like");
          } else {
            // Swipe left - skip
            console.log("Discovery Mode: Swipe left (skip)");
            handleNext("skip");
          }
        } else {
          // Snap back to center
          console.log("Discovery Mode: Snap back to center");
          setDragOffset({ x: 0, y: 0 });
          setDragRotation(0);
        }
      }

      // Reset touch state
      isDragging.current = false;
      touchStartX.current = 0;
      touchStartY.current = 0;
      touchStartTime.current = 0;
    },
    [actionLoading, handleNext]
  );

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setSpaces([]);
      setCurrentIndex(0);
      setSeenSpaces(new Set());
      setEndReached(false);
      setDragOffset({ x: 0, y: 0 });
      setDragRotation(0);
      isDragging.current = false;
      isSwipeInProgress.current = false;
    }
  }, [isOpen]);

  // Get theme color for space
  const getThemeColor = (theme) => {
    if (theme?.colors?.primary) {
      return theme.colors.primary;
    }
    return "#2563eb";
  };

  // Parse bio image URL
  const parseBioImageUrl = (bioImageUrl) => {
    if (!bioImageUrl || bioImageUrl === "null" || bioImageUrl.trim() === "") {
      return null;
    }

    if (bioImageUrl.startsWith("{") && bioImageUrl.includes("url")) {
      try {
        const parsed = JSON.parse(bioImageUrl);
        return parsed.url || null;
      } catch (error) {
        return null;
      }
    }

    return bioImageUrl;
  };

  // Restart discovery
  const handleRestart = async () => {
    setSeenSpaces(new Set());
    setCurrentIndex(0);
    setEndReached(false);
    const newSpaces = await fetchDiscoverySpaces();
    setSpaces(newSpaces);
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-discovery-modal="true"
        data-publish-portal="true"
        style={{
          touchAction: "none", // Prevent default touch behaviors
        }}
        onTouchStart={(e) => {
          // Prevent global touch handlers from interfering
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          // Prevent global touch handlers from interfering
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          // Prevent global touch handlers from interfering
          e.stopPropagation();
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors z-50"
        >
          <FontAwesomeIcon icon={faXmark} className="w-6 h-6" />
        </button>

        {/* Instructions */}
        <div className="absolute top-4 left-4 text-white/80 text-sm z-50">
          <div className="hidden md:block">
            <p>← Skip • ♥ Like →</p>
            <p className="text-xs mt-1">or use X/H keys</p>
          </div>
          <div className="block md:hidden">
            <p>← Swipe left to skip</p>
            <p>Swipe right to like →</p>
          </div>
        </div>

        {/* Main content */}
        <div className="w-full h-full max-w-md max-h-[80vh] relative">
          {loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center text-white">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="w-8 h-8 animate-spin mb-4"
                />
                <p>Finding amazing spaces...</p>
              </div>
            </div>
          ) : !currentSpace ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center text-white">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-xl font-semibold mb-2">
                  No more spaces to discover!
                </h3>
                <p className="text-white/70 mb-4">
                  You've seen all available spaces. Check back later for new
                  discoveries.
                </p>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-accent-lime text-dark-deeper rounded-xl font-medium hover:bg-accent-lime/90 transition-colors"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4 mr-2" />
                  Close Discovery Mode
                </button>
              </div>
            </div>
          ) : (
            <motion.div
              ref={cardRef}
              className="w-full h-full relative"
              style={{
                transform: `translateX(${dragOffset.x}px) translateY(${dragOffset.y}px) rotate(${dragRotation}deg)`,
                touchAction: "none", // Crucial: Prevent default touch behaviors on the card
              }}
              animate={
                isDragging.current
                  ? {}
                  : {
                      x: 0,
                      y: 0,
                      rotate: 0,
                      transition: {
                        type: "spring",
                        stiffness: 500,
                        damping: 40,
                      },
                    }
              }
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Space card */}
              <div
                className="w-full h-full bg-white rounded-3xl overflow-hidden shadow-2xl"
                style={{ touchAction: "none" }} // Also apply to the card itself
              >
                {/* Preview image */}
                <div className="relative h-2/3">
                  {currentSpace.preview_image_url &&
                  !currentSpace.preview_image_url.includes("kong:") ? (
                    <Image
                      src={currentSpace.preview_image_url}
                      alt={`${currentSpace.title} preview`}
                      fill
                      className="object-cover"
                      sizes="400px"
                      priority
                    />
                  ) : currentSpace.preview_image_url &&
                    currentSpace.preview_image_url.includes("kong:") ? (
                    <img
                      src={currentSpace.preview_image_url}
                      alt={`${currentSpace.title} preview`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${getThemeColor(currentSpace.theme)}20, ${getThemeColor(currentSpace.theme)}40)`,
                      }}
                    >
                      <div className="text-center">
                        <div className="text-4xl font-bold text-white/30 mb-2">
                          {currentSpace.title?.charAt(0)?.toUpperCase() || "S"}
                        </div>
                        <div className="text-sm text-white/50">
                          {currentSpace.title}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Gradient overlay */}
                  <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 to-transparent" />

                  {/* Visit space button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/${currentSpace.slug}`, "_blank");
                    }}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-md flex items-center justify-center text-white transition-colors"
                  >
                    <FontAwesomeIcon
                      icon={faExternalLinkAlt}
                      className="w-4 h-4"
                    />
                  </button>
                </div>

                {/* Space info */}
                <div className="p-6 h-1/3 flex flex-col justify-between">
                  <div>
                    {/* Profile image and title */}
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                        {(() => {
                          const bioImageUrl = parseBioImageUrl(
                            currentSpace.bio_image_url
                          );
                          // For local development with Kong URLs or missing images, show placeholder
                          const shouldShowImage =
                            bioImageUrl && !bioImageUrl.includes("kong:");

                          return (
                            <div className="relative w-full h-full">
                              {shouldShowImage && (
                                <img
                                  src={bioImageUrl}
                                  alt={`${currentSpace.title} profile`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Hide the image and show placeholder if it fails to load
                                    e.target.style.display = "none";
                                    const placeholder =
                                      e.target.parentElement.querySelector(
                                        ".bio-placeholder"
                                      );
                                    if (placeholder)
                                      placeholder.style.display = "flex";
                                  }}
                                />
                              )}
                              <div
                                className="bio-placeholder w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold absolute inset-0"
                                style={{
                                  display: shouldShowImage ? "none" : "flex",
                                }}
                              >
                                {currentSpace.title?.charAt(0)?.toUpperCase() ||
                                  "S"}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {currentSpace.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          @{currentSpace.slug}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    {currentSpace.description && (
                      <p className="text-gray-700 text-sm line-clamp-2 mb-3">
                        {currentSpace.description}
                      </p>
                    )}

                    {/* Stats and badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {/* Featured badge */}
                      {currentSpace.is_featured && (
                        <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Featured
                        </span>
                      )}

                      {/* Category badge */}
                      {currentSpace.category && (
                        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                          {currentSpace.category
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      )}

                      {/* Created date */}
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {new Date(currentSpace.created_at).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </div>

                    {/* Tags */}
                    {currentSpace.tags &&
                      Array.isArray(currentSpace.tags) &&
                      currentSpace.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {currentSpace.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={tag.id || index}
                              className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-md"
                            >
                              #{tag.name}
                            </span>
                          ))}
                          {currentSpace.tags.length > 3 && (
                            <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-md">
                              +{currentSpace.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {/* Action indicators */}
              <div
                className="absolute top-1/2 -translate-y-1/2 left-4 opacity-0 pointer-events-none"
                style={{ opacity: Math.max(0, -dragOffset.x / 100) }}
              >
                <div className="w-20 h-20 rounded-full bg-red-500/90 backdrop-blur-md flex flex-col items-center justify-center shadow-xl border-4 border-red-400/50">
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="w-8 h-8 text-white mb-1"
                  />
                  <span className="text-white text-xs font-bold">SKIP</span>
                </div>
              </div>

              <div
                className="absolute top-1/2 -translate-y-1/2 right-4 opacity-0 pointer-events-none"
                style={{ opacity: Math.max(0, dragOffset.x / 100) }}
              >
                <div className="w-20 h-20 rounded-full bg-green-500/90 backdrop-blur-md flex flex-col items-center justify-center shadow-xl border-4 border-green-400/50">
                  <FontAwesomeIcon
                    icon={faHeart}
                    className="w-7 h-7 text-white mb-1"
                  />
                  <span className="text-white text-xs font-bold">LIKE</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Desktop and Mobile action buttons */}
          {!loading && currentSpace && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-6">
              {/* Reject Button */}
              <button
                onClick={() => handleNext("skip")}
                disabled={actionLoading || isSwipeInProgress.current}
                className="group relative w-16 h-16 rounded-full bg-red-500/20 hover:bg-red-500/30 backdrop-blur-md flex flex-col items-center justify-center text-white transition-all duration-200 disabled:opacity-50 hover:scale-110"
              >
                <FontAwesomeIcon icon={faXmark} className="w-7 h-7 mb-0.5" />
                <span className="text-xs font-medium opacity-90">Skip</span>
                <div className="absolute inset-0 rounded-full border-2 border-red-400/30 group-hover:border-red-400/60 transition-colors"></div>
              </button>

              {/* Like Button */}
              <button
                onClick={() => handleNext("like")}
                disabled={actionLoading || isSwipeInProgress.current}
                className="group relative w-16 h-16 rounded-full bg-green-500/20 hover:bg-green-500/30 backdrop-blur-md flex flex-col items-center justify-center text-white transition-all duration-200 disabled:opacity-50 hover:scale-110"
              >
                {actionLoading ? (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="w-6 h-6 animate-spin"
                  />
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faHeart}
                      className="w-6 h-6 mb-0.5"
                    />
                    <span className="text-xs font-medium opacity-90">Like</span>
                  </>
                )}
                <div className="absolute inset-0 rounded-full border-2 border-green-400/30 group-hover:border-green-400/60 transition-colors"></div>
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return typeof window !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
}
